import { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { MapPin, Heart } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InspirationPage = () => {
  const [realWeddings, setRealWeddings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');

  const filters = ['all', 'Traditional', 'Modern', 'Outdoor', 'Elegant', 'Colorful'];

  useEffect(() => {
    fetchRealWeddings();
  }, [activeFilter]);

  const fetchRealWeddings = async () => {
    try {
      const params = activeFilter !== 'all' ? `?style=${activeFilter}` : '';
      const response = await axios.get(`${API}/inspiration/real-weddings${params}`);
      setRealWeddings(response.data || []);
    } catch (error) {
      toast.error('Failed to load weddings');
    } finally {
      setLoading(false);
    }
  };

  // Gallery images for inspiration
  const galleryImages = [
    { url: 'https://images.unsplash.com/photo-1645856052181-64e9dfd9ae03?w=600', category: 'Couple' },
    { url: 'https://images.unsplash.com/photo-1761141988322-9e80cc2345b9?w=600', category: 'Decor' },
    { url: 'https://images.unsplash.com/photo-1742147173338-6cc43624adb8?w=600', category: 'Mehndi' },
    { url: 'https://images.unsplash.com/photo-1583939003579-730e3918a45a?w=600', category: 'Bridal' },
    { url: 'https://images.unsplash.com/photo-1519741497674-611481863552?w=600', category: 'Ceremony' },
    { url: 'https://images.unsplash.com/photo-1537633552985-df8429e8048b?w=600', category: 'Photography' },
    { url: 'https://images.unsplash.com/photo-1522673607200-164d1b6ce486?w=600', category: 'Bridal' },
    { url: 'https://images.unsplash.com/photo-1519225421980-715cb0215aed?w=600', category: 'Decor' },
    { url: 'https://images.unsplash.com/photo-1739958370689-638b27349d1c?w=600', category: 'Food' },
    { url: 'https://images.unsplash.com/photo-1723879580148-517048db5bd9?w=600', category: 'Jewelry' },
    { url: 'https://images.unsplash.com/photo-1600003014755-ba31aa59c4b6?w=600', category: 'Mehndi' },
    { url: 'https://images.unsplash.com/photo-1610173826608-60e8f5c98e75?w=600', category: 'Bridal' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50/50 to-white">
      {/* Header */}
      <div className="bg-white border-b border-pink-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <h1 className="font-heading text-4xl lg:text-5xl font-bold mb-4">
              Wedding <span className="gradient-text">Inspiration</span>
            </h1>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Get inspired by real South Asian weddings and stunning photo galleries
            </p>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Real Weddings Section */}
        <section className="mb-16">
          <div className="flex items-center justify-between mb-8">
            <h2 className="font-heading text-2xl lg:text-3xl font-bold">
              Real Weddings
            </h2>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-2 mb-8">
            {filters.map((filter) => (
              <Button
                key={filter}
                variant={activeFilter === filter ? 'default' : 'outline'}
                onClick={() => setActiveFilter(filter)}
                className={`rounded-full capitalize ${
                  activeFilter === filter 
                    ? 'bg-primary text-white' 
                    : 'border-pink-200 hover:bg-pink-50'
                }`}
                data-testid={`filter-${filter}`}
              >
                {filter}
              </Button>
            ))}
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(3)].map((_, i) => (
                <div key={`insp-skel-${i}`} className="aspect-[4/5] bg-pink-100 rounded-3xl animate-pulse" />
              ))}
            </div>
          ) : realWeddings.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No weddings found for this filter</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {realWeddings.map((wedding, idx) => (
                <motion.article
                  key={wedding.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  className="group cursor-pointer"
                  data-testid={`wedding-card-${wedding.id}`}
                >
                  <div className="relative rounded-3xl overflow-hidden aspect-[4/5] img-zoom">
                    <img
                      src={wedding.images?.[0] || 'https://images.unsplash.com/photo-1645856052181-64e9dfd9ae03?w=600'}
                      alt={wedding.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                    
                    {/* Save Button */}
                    <button className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <Heart className="w-5 h-5 text-pink-500" />
                    </button>

                    {/* Content */}
                    <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                      <h3 className="font-heading font-bold text-xl mb-2">{wedding.couple_names}</h3>
                      <p className="text-white/80 flex items-center gap-1 mb-3">
                        <MapPin className="w-4 h-4" />
                        {wedding.location}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {wedding.style_tags?.slice(0, 3).map((tag) => (
                          <Badge key={tag} className="bg-white/20 text-white border-0 backdrop-blur-sm">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          )}
        </section>

        {/* Photo Gallery Section */}
        <section>
          <div className="flex items-center justify-between mb-8">
            <h2 className="font-heading text-2xl lg:text-3xl font-bold">
              Photo <span className="gradient-text">Gallery</span>
            </h2>
          </div>

          {/* Masonry Gallery */}
          <div className="masonry">
            {galleryImages.map((image, idx) => (
              <motion.div
                key={image.id || `gallery-${idx}`}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.05 }}
                className="masonry-item group cursor-pointer"
              >
                <div className="relative rounded-2xl overflow-hidden img-zoom">
                  <img
                    src={image.url}
                    alt={image.category}
                    className={`w-full object-cover ${
                      idx % 3 === 0 ? 'aspect-[3/4]' : idx % 3 === 1 ? 'aspect-square' : 'aspect-[4/3]'
                    }`}
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                    <Badge className="bg-white/90 text-foreground opacity-0 group-hover:opacity-100 transition-opacity">
                      {image.category}
                    </Badge>
                  </div>
                  <button className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <Heart className="w-4 h-4 text-pink-500" />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default InspirationPage;
